import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { InjectionToken, ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { RouterModule, UrlSerializer } from '@angular/router';
import { AtlasModule } from '@galaxy/atlas';
import { InboxButtonModule } from '@galaxy/conversation/button';
import { LexiconModule } from '@galaxy/lexicon';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyInfiniteScrollTriggerModule } from '@vendasta/galaxy/infinite-scroll-trigger';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import * as baseTranslation from './assets/i18n/en_devel.json';
import { BcNavItemComponent } from './bc-nav-item/bc-nav-item.component';
import { BusinessSidebarComponent } from './business-sidebar/business-sidebar.component';
import { BusinessNavChipComponent } from './core-components/chip/chip.component';
import { EncodeBrandNamePipe } from './core-components/encode-brand.pipe';
import { ButtonComponent } from './core-components/form-controls/button/button.component';
import { SearchBoxComponent } from './core-components/form-controls/search-box/search-box.component';
import { TextareaComponent } from './core-components/form-controls/text-area/textarea.component';
import { IconComponent } from './core-components/icon/icon.component';
import { LoaderComponent } from './core-components/loader/loader.component';
import { NavItemContainerComponent } from './core-components/nav-item-container/nav-item-container.component';
import { NavItemSectionComponent } from './core-components/nav-item-section/nav-item-section.component';
import { NavItemComponent } from './core-components/nav-item/nav-item.component';
import { SidebarComponent } from './core-components/sidebar/sidebar.component';
import { SnackbarComponent } from './core-components/snackbar/snackbar.component';
import { AtlasTooltipModule } from './core-components/tooltip/tooltip-module';
import { CustomUrlSerializer } from './custom-url-serializer';
import { LocationSelectorComponent } from './location-selector/location-selector.component';
import { LocationListItemComponent } from './modals/location-switcher-modal/location-list/location-list-item/location-list-item.component';
import { LocationListComponent } from './modals/location-switcher-modal/location-list/location-list.component';
import { LocationSwitcherModalComponent } from './modals/location-switcher-modal/location-switcher-modal.component';
import { TabHeaderComponent } from './modals/location-switcher-modal/tab-header/tab-header.component';
import { LocationTabComponent } from './modals/location-switcher-modal/tab/tab.component';
import { MeetingBookingUrlModule } from './modals/sales-info/meeting-booking-url/meeting-booking-url.component';
import { SalesInfoModalComponent } from './modals/sales-info/sales-info.modal.component';
import { UserRequiredModalComponent } from './modals/user-required/user-required.modal.component';
import { NavigationComponent } from './navigation/navigation.component';

type BusinessNavModuleConfig = {
  locationSwitchConfig: {
    // The keys to use for the location params
    supportedLocationContextConfig: {
      // The location context this config is applicable to
      context: 'brand' | 'location';
      // The url param with the associated context key
      locationParamKey: string;
    }[];
    // Where to go if no location is set. Not a replacement for auth guards.
    defaultPath: string;
    // Disabled path regexes
    blockListedPaths: RegExp[];
  };
};

// Do not provide this directly, instead call BusinessNavModule.forRoot({ locationSwitchConfig: ... })
export const LOCATION_SWITCH_CONFIG_TOKEN = new InjectionToken<BusinessNavModuleConfig['locationSwitchConfig']>(
  'LOCATION_SWITCH_CONFIG',
);

@NgModule({
  declarations: [
    NavigationComponent,
    SidebarComponent,
    NavItemComponent,
    IconComponent,
    BusinessSidebarComponent,
    NavItemContainerComponent,
    NavItemSectionComponent,
    BcNavItemComponent,
    SalesInfoModalComponent,
    UserRequiredModalComponent,
    TextareaComponent,
    ButtonComponent,
    LocationSelectorComponent,
    LocationSwitcherModalComponent,
    SearchBoxComponent,
    LocationTabComponent,
    LocationListComponent,
    LocationListItemComponent,
    EncodeBrandNamePipe,
    LoaderComponent,
    SnackbarComponent,
    BusinessNavChipComponent,
    TabHeaderComponent,
  ],
  providers: [
    {
      provide: UrlSerializer,
      useClass: CustomUrlSerializer,
    },
  ],
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    AtlasModule,
    DragDropModule,
    AtlasTooltipModule,
    GalaxyEmptyStateModule,
    GalaxyInfiniteScrollTriggerModule,
    InboxButtonModule,
    NgOptimizedImage,
    MeetingBookingUrlModule,
    MatIconModule,
    GalaxyAvatarModule,
    MatButtonModule,
    MatMenuModule,
    MatListModule,
    GalaxyBadgeModule,
    TranslateModule,
    MatDialogModule,
    GalaxyFormFieldModule,
    MatInputModule,
    GalaxyButtonLoadingIndicatorModule,
    GalaxyTooltipModule,
    LexiconModule.forChild({
      componentName: 'common/business-nav',
      baseTranslation,
    }),
  ],
  exports: [NavigationComponent, NavItemComponent, NavItemContainerComponent, AtlasModule, LocationListComponent],
})
export class BusinessNavModule {
  static forRoot({ locationSwitchConfig }: BusinessNavModuleConfig): ModuleWithProviders<BusinessNavModule> {
    locationSwitchConfig.blockListedPaths = cleanRegex([
      ...locationSwitchConfig.blockListedPaths,
      ...GlobalBlockListedPaths,
    ]);
    return {
      ngModule: BusinessNavModule,
      providers: [
        {
          provide: LOCATION_SWITCH_CONFIG_TOKEN,
          useValue: locationSwitchConfig,
        },
      ],
    };
  }
}

function cleanRegex(regexes: RegExp[]): RegExp[] {
  // remove the global flag from the regexes because its presence causes the regex `test` method
  // to mutate the regex's `lastIndex` property, which causes the regex to fail on subsequent calls
  // see:
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/lastIndex#description
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/test
  return regexes.map((r) => new RegExp(r.source, r.flags.replace('g', '')));
}

/**
 * TODO (BREW-230): List of paths that should not be replaced with the current location due to data leakage or initialization logic that breaks
 * when the path is changed. These are bugs that should be fixed, but for now we need to block them so users don't suffer.
 */
const GlobalBlockListedPaths: RegExp[] = [/\/crm\//i];
